(() => {
  const CONCEPT_ANIMATION_VISIBLE_CLASS = 'aureme-concept__motion-visible';
  /* Concept ページの段階表示対象を motion effect の data 属性でまとめて扱う */
  const CONCEPT_ANIMATION_TARGET_SELECTOR = '[data-aureme-concept-motion-effect]';
  /* IO の rootMargin -12% と揃え、viewport 下端 12% に掛かる要素は初期表示でも未視認として扱う */
  const CONCEPT_INITIAL_VISIBLE_VIEWPORT_RATIO = 0.88;
  const DESKTOP_CONCEPT_VIEWPORT_QUERY = '(min-width: 750px)';
  const DESKTOP_SCROLL_DEFER_VALUE = 'desktop-first-scroll';
  const FULL_VIEWPORT_INITIAL_VALUE = 'full-viewport';
  const SCROLL_REVEAL_INTERACTIVE_TARGET_SELECTOR =
    'input, textarea, select, button, a[href], summary, [role="button"], [role="link"], [role="menuitem"], [role="tab"]';
  const SCROLL_REVEAL_KEYS = new Set([' ', 'ArrowDown', 'End', 'PageDown', 'Spacebar']);

  function getConceptSections(root = document) {
    const sections = Array.from(root.querySelectorAll('.aureme-concept'));
    if (root instanceof Element && root.matches('.aureme-concept')) {
      sections.unshift(root);
    }

    return sections;
  }

  function getAnimatedConceptSections(root = document) {
    return getConceptSections(root).filter((section) =>
      section.classList.contains('aureme-concept--animated'),
    );
  }

  function getConceptAnimationTargets(section) {
    return section.querySelectorAll(CONCEPT_ANIMATION_TARGET_SELECTOR);
  }

  function isConceptAnimationTargetInViewport(element) {
    return isConceptAnimationTargetVisibleBefore(element, 1);
  }

  function isConceptAnimationTargetVisibleBefore(element, viewportRatio) {
    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    if (!viewportHeight || rect.height <= 0) return false;

    return rect.bottom > 0 && rect.top < viewportHeight * viewportRatio;
  }

  function isDesktopConceptViewport() {
    return (
      typeof window.matchMedia === 'function' &&
      window.matchMedia(DESKTOP_CONCEPT_VIEWPORT_QUERY).matches
    );
  }

  function isConceptPageScrolled() {
    const scrollTop =
      window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
    return scrollTop > 0;
  }

  function isConceptAnchorNavigation() {
    return Boolean(window.location.hash);
  }

  function isFirstViewScrollDeferredTarget(element) {
    return element.dataset.auremeConceptMotionDefer === DESKTOP_SCROLL_DEFER_VALUE;
  }

  function getInitialVisibleViewportRatio(element, fallbackViewportRatio) {
    if (element.dataset.auremeConceptMotionInitial === FULL_VIEWPORT_INITIAL_VALUE) {
      return 1;
    }

    return fallbackViewportRatio;
  }

  function isSamePageHashLinkEvent(event) {
    const target = event.target;
    if (!(target instanceof Element)) return false;

    const anchor = target.closest('a[href]');
    if (!(anchor instanceof HTMLAnchorElement) || !anchor.hash) return false;

    return (
      anchor.origin === window.location.origin &&
      anchor.pathname === window.location.pathname &&
      anchor.search === window.location.search
    );
  }

  function isInteractiveEventTarget(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return false;

    return Boolean(
      target.isContentEditable || target.closest(SCROLL_REVEAL_INTERACTIVE_TARGET_SELECTOR),
    );
  }

  function isScrollRevealKey(event) {
    if (event.altKey || event.ctrlKey || event.metaKey) return false;
    if (isInteractiveEventTarget(event)) return false;

    return SCROLL_REVEAL_KEYS.has(event.key);
  }

  const conceptAnimationMode = {
    getStorageKey() {
      return document.documentElement.dataset.auremeConceptAnimationStorageKey;
    },

    get() {
      return document.documentElement.dataset.auremeConceptAnimationState || 'static';
    },

    writeSeenState() {
      const root = document.documentElement;
      if (root.dataset.auremeConceptAnimationWritten === 'true') return;
      const storageKey = conceptAnimationMode.getStorageKey();
      if (!storageKey) return;

      try {
        const playedAt = Date.now();
        window.localStorage.setItem(
          storageKey,
          JSON.stringify({
            playedAt,
          }),
        );
      } catch {}

      root.dataset.auremeConceptAnimationWritten = 'true';
    },

    setSeenMode() {
      const root = document.documentElement;
      root.classList.remove('aureme-concept-motion-ready');
      root.classList.add('aureme-concept-seen');
      root.dataset.auremeConceptAnimationState = 'seen';
    },

    refresh() {
      const refreshMode = window.AuremeConceptAnimation?.refreshMode;
      if (typeof refreshMode !== 'function') return false;

      try {
        refreshMode();
        return true;
      } catch {
        return false;
      }
    },
  };

  /*
   * PC 初回表示では first view 内の一部 motion を最初のスクロールまで抑止する。
   * 戻る/アンカー/リサイズ時は gate を完了させ、未表示対象を通常の IO 監視へ戻す。
   */
  const firstViewScrollGate = (() => {
    let currentGate = null;

    function stop() {
      currentGate?.abort();
      currentGate = null;
    }

    function create({
      observer,
      deferredFirstViewTargets,
      pendingFirstScrollRevealTargets,
      revealTarget,
    }) {
      const controller = new AbortController();
      const { signal } = controller;
      const gate = {
        abort() {
          controller.abort();
        },
      };
      currentGate = gate;

      function isCurrent() {
        return !signal.aborted && currentGate === gate;
      }

      function stopCurrentGate() {
        controller.abort();
        currentGate = null;
      }

      function clearTargets() {
        deferredFirstViewTargets.clear();
        pendingFirstScrollRevealTargets.clear();
      }

      function finishGate(runAfterGateStops) {
        if (!isCurrent()) return;

        stopCurrentGate();
        runAfterGateStops();
        clearTargets();
      }

      function releaseGate() {
        finishGate(() => {
          for (const element of pendingFirstScrollRevealTargets) {
            revealTarget(element);
          }
        });
      }

      function completeGate() {
        finishGate(() => {
          for (const element of deferredFirstViewTargets) {
            if (isConceptAnimationTargetInViewport(element)) {
              observer.unobserve(element);
              revealTarget(element);
              continue;
            }

            observer.observe(element);
          }
        });
      }

      /* リサイズで first view 内へ入った対象は次スクロールの表示待ちへ移し、外へ出た対象は IO 監視へ戻す */
      function keepDesktopTargetsDeferred() {
        for (const element of deferredFirstViewTargets) {
          if (isConceptAnimationTargetInViewport(element)) {
            observer.unobserve(element);
            pendingFirstScrollRevealTargets.add(element);
          } else if (pendingFirstScrollRevealTargets.has(element)) {
            observer.observe(element);
            pendingFirstScrollRevealTargets.delete(element);
          }

          element.classList.remove(CONCEPT_ANIMATION_VISIBLE_CLASS);
        }
      }

      const passiveOnceOptions = { once: true, passive: true, signal };

      function handleKeydown(event) {
        if (!isScrollRevealKey(event)) return;

        releaseGate();
      }

      function handleResize() {
        if (isDesktopConceptViewport()) {
          keepDesktopTargetsDeferred();
          return;
        }

        completeGate();
      }

      function handleHashLinkClick(event) {
        if (!isSamePageHashLinkEvent(event)) return;

        completeGate();
      }

      window.addEventListener('wheel', releaseGate, passiveOnceOptions);
      window.addEventListener('touchmove', releaseGate, passiveOnceOptions);
      window.addEventListener('keydown', handleKeydown, { signal });
      window.addEventListener('resize', handleResize, { signal });
      window.addEventListener('hashchange', completeGate, { signal });
      document.addEventListener('click', handleHashLinkClick, {
        capture: true,
        signal,
      });

      requestAnimationFrame(() => {
        if (!isCurrent()) return;

        if (isConceptPageScrolled()) {
          completeGate();
          return;
        }

        const scrollYWhenArmed = window.scrollY;
        const scrollOptions = { passive: true, signal };

        function handleScroll() {
          /* アンカー補正などで位置が変わらない scroll イベントでは解除せず、実際に動いたときだけ gate を解く */
          if (window.scrollY === scrollYWhenArmed) return;

          releaseGate();
        }

        window.addEventListener('scroll', handleScroll, scrollOptions);
      });
    }

    function isActive() {
      return currentGate != null;
    }

    return {
      create,
      isActive,
      stop,
    };
  })();

  const conceptMotionObserver = (() => {
    let observer = null;

    function stop() {
      firstViewScrollGate.stop();
      observer?.disconnect();
      observer = null;
    }

    function revealTargets(root = document) {
      for (const section of getAnimatedConceptSections(root)) {
        for (const element of getConceptAnimationTargets(section)) {
          element.classList.add(CONCEPT_ANIMATION_VISIBLE_CLASS);
        }
      }
    }

    function resetTargets(root = document) {
      for (const element of root.querySelectorAll(CONCEPT_ANIMATION_TARGET_SELECTOR)) {
        element.classList.remove(CONCEPT_ANIMATION_VISIBLE_CLASS);
      }
    }

    /**
     * 実際に見えた要素を起点に再訪判定を書き込み、
     * 未視認のままクールダウンだけ始まる状態を避ける。
     * @param {Element} element
     */
    function revealTarget(element) {
      conceptAnimationMode.writeSeenState();
      element.classList.add(CONCEPT_ANIMATION_VISIBLE_CLASS);
    }

    function start(
      root = document,
      animatedConceptSections = getAnimatedConceptSections(root),
      { deferInitialFirstViewTargets = true } = {},
    ) {
      stop();

      if (!animatedConceptSections.length) return;
      const isDesktopViewport = isDesktopConceptViewport();
      const isScrolled = isConceptPageScrolled();
      const isAnchorNavigation = isConceptAnchorNavigation();
      const shouldDeferFirstViewTargets =
        deferInitialFirstViewTargets && isDesktopViewport && !isScrolled && !isAnchorNavigation;
      const initialVisibleViewportRatio =
        isDesktopViewport && !shouldDeferFirstViewTargets
          ? 1
          : CONCEPT_INITIAL_VISIBLE_VIEWPORT_RATIO;
      // gate はスクロール前の IO 表示抑止、pending は次のスクロールで実際に表示する対象を持つ。
      const deferredFirstViewTargets = new Set();
      const pendingFirstScrollRevealTargets = new Set();
      const initiallyVisibleTargets = [];

      observer = new IntersectionObserver(
        (entries, currentObserver) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;

            if (
              shouldDeferFirstViewTargets &&
              firstViewScrollGate.isActive() &&
              deferredFirstViewTargets.has(entry.target)
            ) {
              pendingFirstScrollRevealTargets.add(entry.target);
              currentObserver.unobserve(entry.target);
              continue;
            }

            revealTarget(entry.target);
            currentObserver.unobserve(entry.target);
          }
        },
        {
          rootMargin: '0px 0px -12% 0px',
          threshold: 0.15,
        },
      );

      for (const section of animatedConceptSections) {
        for (const element of getConceptAnimationTargets(section)) {
          if (shouldDeferFirstViewTargets && isFirstViewScrollDeferredTarget(element)) {
            deferredFirstViewTargets.add(element);
            if (isConceptAnimationTargetInViewport(element)) {
              pendingFirstScrollRevealTargets.add(element);
            } else {
              observer.observe(element);
            }
            continue;
          }

          if (
            isConceptAnimationTargetVisibleBefore(
              element,
              getInitialVisibleViewportRatio(element, initialVisibleViewportRatio),
            )
          ) {
            initiallyVisibleTargets.push(element);
            continue;
          }

          observer.observe(element);
        }
      }

      for (const element of initiallyVisibleTargets) {
        revealTarget(element);
      }

      if (deferredFirstViewTargets.size) {
        firstViewScrollGate.create({
          observer,
          deferredFirstViewTargets,
          pendingFirstScrollRevealTargets,
          revealTarget,
        });
      }
    }

    function initialize(root = document, options = {}) {
      const animatedConceptSections = getAnimatedConceptSections(root);

      if (!animatedConceptSections.length) {
        stop();
        return;
      }

      if (conceptAnimationMode.get() !== 'animate') {
        stop();
        revealTargets(root);
        return;
      }

      start(root, animatedConceptSections, options);
    }

    return {
      initialize,
      resetTargets,
      stop,
    };
  })();

  const conceptPageLifecycle = (() => {
    function runWhenVisible(callback) {
      if (document.visibilityState === 'visible') {
        callback();
        return;
      }

      function handleVisibilityChange() {
        if (document.visibilityState !== 'visible') return;

        document.removeEventListener('visibilitychange', handleVisibilityChange);
        callback();
      }

      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    function initializeWhenVisible(
      root = document,
      { beforeInitialize, deferInitialFirstViewTargets = true } = {},
    ) {
      runWhenVisible(() => {
        beforeInitialize?.();
        conceptMotionObserver.initialize(root, { deferInitialFirstViewTargets });
      });
    }

    function scheduleSetup() {
      // 背景タブでは可視化されるまで初回表示扱いにしない。
      function initializeDocumentWhenVisible() {
        initializeWhenVisible(document);
      }

      // DOM 構築済みならそのまま初期化し、未完了なら DOMContentLoaded を待つ
      if (document.readyState !== 'loading') {
        initializeDocumentWhenVisible();
        return;
      }

      document.addEventListener('DOMContentLoaded', initializeDocumentWhenVisible, { once: true });
    }

    function restoreFromBFCache() {
      const root = document.documentElement;
      if (
        !conceptAnimationMode.refresh() &&
        root.dataset.auremeConceptAnimationWritten === 'true'
      ) {
        conceptAnimationMode.setSeenMode();
      }

      delete root.dataset.auremeConceptAnimationWritten;
      conceptMotionObserver.resetTargets(document);
    }

    function bindBFCacheRestore() {
      // BFCache 復帰時は asset が再実行されないため、監視と state をここで立て直す。
      window.addEventListener('pageshow', (event) => {
        if (!event.persisted || !getAnimatedConceptSections().length) return;

        initializeWhenVisible(document, {
          beforeInitialize: restoreFromBFCache,
        });
      });
    }

    function bindThemeEditorReload() {
      // Theme Editor で section が差し替わったときも、差し替え後の要素を再監視する。
      document.addEventListener('shopify:section:load', (event) => {
        if (!getConceptSections(event.target).length) return;

        initializeWhenVisible(event.target, {
          deferInitialFirstViewTargets: false,
          beforeInitialize: () => {
            conceptMotionObserver.resetTargets(event.target);
          },
        });
      });
    }

    function start() {
      scheduleSetup();
      bindBFCacheRestore();
      bindThemeEditorReload();
    }

    return {
      start,
    };
  })();

  conceptPageLifecycle.start();
})();
